<script setup lang="ts">
import type { AgentOnList } from '@@/shared/agent/types';

const props = defineProps<{
  agent: AgentOnList
  variant: 'agent-card' | 'agent-page' | 'property-page'
}>();

// HIGHT QUALITY LAYOUT

const imageWidth = computed(() => {
  if (props.variant === 'agent-card') return 400;
  if (props.variant === 'agent-page') return 320;
  return 160;
});
const avatarUrl = useImageResize({
  width: imageWidth,
  src: toRef(() => props.agent.avatar.src),
});

// LOW QUALITY LAYOUT

const SMALL_IMG_WIDTH = 144;
const smallAvatarUrl = useImageResize({
  width: SMALL_IMG_WIDTH,
  src: toRef(() => props.agent.avatar.src),
  factors: 1,
});

const runtimeConfig = useRuntimeConfig();
const background = computed(() => {
  const { src: resizedUrl } = getImageResize({
    width: 50,
    src: props.agent.avatar.src,
    factors: 1,
    resizeHostname: runtimeConfig.public.img.resizeHostname,
  });
  return `background-image: url('${resizedUrl}');`;
});
</script>

<template>
  <figure
    class="flex justify-center items-center relative overflow-clip"
    :class="{
      'aspect-[4/3]': variant === 'agent-card',
      'aspect-[4/3] md:aspect-square w-full md:max-w-[325px] md:rounded-2xl': variant === 'agent-page',
      'aspect-square': variant === 'property-page',
    }"
  >
    <img
      v-if="agent.avatar.isOfGoodQuality"
      loading="lazy"
      v-bind="avatarUrl"
      class="size-full object-cover"
      :alt="agent.fullName"
      :title="agent.fullName"
      :class="{
        'aspect-[4/3]': variant === 'agent-card',
        'aspect-[4/3] md:aspect-square md:rounded-2xl': variant === 'agent-page',
        'aspect-square': variant === 'property-page',
      }"
    >
    <template v-else>
      <i
        class="absolute inset-0 bg-blue bg-cover bg-center scale-150 blur-2xl"
        :style="background"
      />
      <div class="size-[144px] relative">
        <img
          :height="SMALL_IMG_WIDTH"
          loading="lazy"
          :alt="agent.fullName"
          :title="agent.fullName"
          :width="SMALL_IMG_WIDTH"
          :src="smallAvatarUrl.src"
          class="size-full object-cover aspect-square rounded-3xl"
        >
      </div>
    </template>
  </figure>
</template>
